import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Input from './Input';
import Textarea from './Textarea';
import Picklist from './Picklist';
import Checkbox from './Checkbox';
import InputWrapper from './InputWrapper';

const useStyles = makeStyles(theme => ({
  tableContainer: {
    overflowX: 'auto',
    marginTop: theme.spacing(1)
  },
  table: {
    minWidth: 650,
    '& .MuiTableCell-root': {
      padding: theme.spacing(1),
      verticalAlign: 'top'
    }
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100],
    fontSize: '1.6rem',
    fontFamily: 'Nunito'
  },
  cellInput: {
    margin: 0,
    '& input, & textarea, & select': {
      fontSize: '1.4rem'
    }
  }
}));

/**
 * Table component for redux-form
 * Renders a table with editable cells based on column definitions
 * Cells with initial values are readonly
 */
const TableInput = props => {
  const classes = useStyles();
  const {input, fieldMetadata} = props;
  const {columns = [], initialRows = []} = fieldMetadata || {};

  // Initialize value from input or use initialRows
  const rows = input.value || initialRows;

  // Handle cell value change
  const handleCellChange = (rowIndex, columnName, newValue) => {
    const updatedRows = [...rows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [columnName]: newValue
    };
    input.onChange(updatedRows);
  };

  // Determine if a cell should be readonly (has initial value)
  const isCellReadOnly = (rowIndex, columnName) => {
    if (!initialRows[rowIndex]) return false;
    const initialValue = initialRows[rowIndex][columnName];
    return (
      initialValue !== undefined && initialValue !== null && initialValue !== ''
    );
  };

  // Get the appropriate component for a column type
  const getComponentForType = type => {
    switch (type) {
      case 'textarea':
        return Textarea;
      case 'picklist':
        return Picklist;
      case 'checkbox':
        return Checkbox;
      case 'number':
      case 'date':
      case 'text':
      default:
        return Input;
    }
  };

  // Render a cell input
  const renderCellInput = (row, rowIndex, column) => {
    const {name, type, picklist} = column;
    const value = row[name] || '';
    const isReadOnly = isCellReadOnly(rowIndex, name);
    const Component = getComponentForType(type);

    // Create minimal field metadata for the cell
    const cellMetadata = {
      picklist: picklist || []
    };

    // Props for the input component
    const inputProps = {
      value: value,
      onChange: newValue => {
        // Handle different onChange signatures
        if (typeof newValue === 'object' && newValue.target) {
          // Standard input onChange event
          handleCellChange(rowIndex, name, newValue.target.value);
        } else {
          // Direct value (e.g., from Picklist)
          handleCellChange(rowIndex, name, newValue);
        }
      },
      onBlur: () => {},
      onFocus: () => {},
      name: `${input.name}[${rowIndex}].${name}`
    };

    const componentProps = {
      input: inputProps,
      fieldMetadata: cellMetadata,
      readOnly: isReadOnly,
      className: classes.cellInput
    };

    // Add type prop for Input component
    if (Component === Input) {
      if (type === 'number') {
        componentProps.type = 'number';
      } else if (type === 'date') {
        componentProps.type = 'date';
      } else {
        componentProps.type = 'text';
      }
    }

    return <Component {...componentProps} />;
  };

  return (
    <InputWrapper {...props}>
      <div className={classes.tableContainer}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.name} className={classes.headerCell}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map(column => (
                  <TableCell key={column.name}>
                    {renderCellInput(row, rowIndex, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </InputWrapper>
  );
};

TableInput.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    name: PropTypes.string.isRequired
  }).isRequired,
  fieldMetadata: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        picklist: PropTypes.arrayOf(PropTypes.string)
      })
    ),
    initialRows: PropTypes.arrayOf(PropTypes.object)
  }),
  label: PropTypes.string
};

export default TableInput;
